import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
// Utils / Constants
import equal from 'NewAngular/utils/equal/deep-equal';
import { convertStringToProperType, formFlattenedObject, parseFlattenedObject } from 'NewAngular/utils/urls';
import { shallowMergeAppStates } from 'State/app_state/utils';
// NgRx actions / selectors
import { resetToSystemDefault, resetToUserDefault, setAppStateSliceKey, setDeserializedAppState, } from 'NewAngular/state/app-state/app-state.actions';
import * as i0 from "@angular/core";
import * as i1 from "@angular/router";
import * as i2 from "@ngrx/store";
/**
 * App State Service
 *
 */
export class AppStateService {
    constructor(route, router, store) {
        this.route = route;
        this.router = router;
        this.store = store;
        // Set by each app
        this.stateSliceKey = '';
        this.urlParamKeys = [];
        this.currentSerializedState = {};
        this.customSerializer = {};
        this.customDeserializer = {};
        // A guided workflow ID may be passed in the URL to specify a workflow to open in the side menu when the page loads.
        this.reservedGuidedWorkflowKey = 'gw';
        // Query params that add functionality to the app but are not part of the apps state logic.
        this.statelessQueryParams = [this.reservedGuidedWorkflowKey];
        // Set to true after the state has been set initially from URL params or user preferences
        this.enabled = false;
        this.storeSubscription = null;
        this.queryParamMapSubscription = null;
    }
    serialize(state) {
        return formFlattenedObject(state, this.urlParamKeys, this.customSerializer);
    }
    deserialize(urlParams) {
        return parseFlattenedObject(urlParams, this.urlParamKeys, this.customDeserializer);
    }
    setStateSlice(sliceKey = '') {
        this.stateSliceKey = sliceKey;
    }
    setUrlParamKeys(urlKeys) {
        this.urlParamKeys = urlKeys;
    }
    setCustomUrlParamDeserializer(deserializer) {
        this.customDeserializer = deserializer;
    }
    setCustomUrlParamSerializer(serializer) {
        this.customSerializer = serializer;
    }
    resetToSystemDefaultState() {
        this.store.dispatch(resetToSystemDefault());
    }
    start() {
        this.unsubscribe();
        this.store.dispatch(setAppStateSliceKey({ stateSliceKey: this.stateSliceKey }));
        let currentUrlParams = this.serialize({});
        // Subscribe to the NgRx store, and change the URL whenever it changes in a relevant way
        this.storeSubscription = this.store
            .select((state) => state.reducers[this.stateSliceKey])
            .subscribe((data) => {
            /*
             * We only allow the URL to be updated after the state has already been updated at least once.
             * Since the store is always initialized with default values on page load,
             * If we allow the URL to be updated on page load before the store,
             * any query params entered by the user could be overwritten by the initial store values.
             */
            if (!this.enabled)
                return;
            const lastUrlParams = currentUrlParams;
            currentUrlParams = this.serialize(data);
            this.currentSerializedState = currentUrlParams;
            let paramHasChanged = false;
            /*
             * Compare the current URL params with what we have in the state, if there's a discrepancy we should switch
             * paramHasChanged to true so that we trigger an update in the location, then the normal pipeline that
             * validates the default params will run.
             * This is mostly to prevent the case when the user removes any required param from the URL and the param
             * gets lost, this makes so we make sure the param gets added back
             */
            const { queryParamMap } = this.route.snapshot;
            for (let i = 0; i < this.urlParamKeys.length; i += 1) {
                const key = this.urlParamKeys[i];
                if (queryParamMap.get(key) === null || lastUrlParams[key] !== currentUrlParams[key]) {
                    paramHasChanged = true;
                    break;
                }
            }
            if (paramHasChanged) {
                this.router.navigate(['.'], {
                    queryParams: currentUrlParams,
                    queryParamsHandling: 'merge',
                });
            }
        });
        // Whenever the url query params change, check if we should dispatch either the resetToUserDefault or
        // setDeserializedAppState actions
        this.queryParamMapSubscription = this.route.queryParamMap.subscribe((data) => {
            // Cast to a QueryParams object which has a `params` attribute
            const urlParams = data.params;
            // The line above returns the params as all string values even when
            // they are a different type in the state, so we need to convert
            // the params to the proper type in order to compare them to the currentSerializedState
            const convertedUrlParams = {};
            const statelessUrlParamKeys = [];
            Object.entries(urlParams).forEach(([key, value]) => {
                if (this.statelessQueryParams.includes(key)) {
                    statelessUrlParamKeys.push(key);
                    return;
                }
                convertedUrlParams[key] = convertStringToProperType(value);
            });
            // If the user has not provided any app state in the URL then we reset
            // to the users default preferences. However, the data parameter to this function
            // will always be empty the first time the function is executed even if there are
            // params present in the URL, and the reason is an implementation detail of Angular.
            // Here's the related github issue where workarounds are discussed: https://github.com/angular/angular/issues/12157
            // To avoid always resetting to user default on the initial execution of this function, we also check
            // if the URL does not contain a '?' character. If '?' is present then we know the user has provided
            // app state in the URL, so we should use that app state and not reset to user default.
            // There's a special case of params that when being the only params present should dispatch
            // 'resetToUserDefault' as these do not affect app state but do trigger other logic in the app.
            const isStatelessQueryParamsOnly = statelessUrlParamKeys.length > 0 &&
                Object.keys(urlParams).every((key) => statelessUrlParamKeys.includes(key));
            if ((equal(convertedUrlParams, {}) && !window.location.href.includes('?')) || isStatelessQueryParamsOnly) {
                this.enabled = true;
                this.store.dispatch(resetToUserDefault());
            }
            else if (!equal(convertedUrlParams, this.currentSerializedState)) {
                this.enabled = true;
                // If user preferences are available, use their value when one isn't already provided.
                // This preserves user preferences even when redirecting with some, but not all, query params.
                const deserializedAppState = window.userProvidedAppState
                    ? shallowMergeAppStates(window.userProvidedAppState, this.deserialize(convertedUrlParams))
                    : this.deserialize(convertedUrlParams);
                this.store.dispatch(setDeserializedAppState({ deserializedAppState }));
            }
        });
    }
    unsubscribe() {
        if (this.storeSubscription)
            this.storeSubscription.unsubscribe();
        if (this.queryParamMapSubscription)
            this.queryParamMapSubscription.unsubscribe();
    }
    ngOnDestroy() {
        this.storeSubscription.unsubscribe();
    }
}
AppStateService.ɵfac = function AppStateService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AppStateService)(i0.ɵɵinject(i1.ActivatedRoute), i0.ɵɵinject(i1.Router), i0.ɵɵinject(i2.Store)); };
AppStateService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AppStateService, factory: AppStateService.ɵfac, providedIn: 'root' });
